import { LanguageJson, Language } from "./Langauges";
export enum GeographyType {
  Country = "Country",
  State = "State",
  County = "County",
}

export const stateLabelMappings: { [key: string]: string } = {
  "united-states": "State",
  canada: "Province/Territory",
};

export interface StateContentJson {
  id: number;
  name: string;
  code: string;
  states_id: number;
  language?: LanguageJson;
  languages_id?: number;
  // state_obituary_requirement: string,
  // state_small_estate_requirement: string,
  // state_statute_of_limitation: string,
  // state_executor_compensation: string,
  // state_inventory_appraisal_filing: string,
  // obituary_legal_source: string,
  // small_legal_source: string,
  // statute_legal_source: string,
  // executor_legal_source: string,
  // inventory_legal_source: string,
  value_threshold?: number;
  has_probate_definitions?: boolean;
  probate_fallback_content?: string;
}

export class StateContent {
  constructor(
    public name: string,
    public code: string,
    public id: number,
    public stateId: number,
    public language?: Language,
    public valueThreshold?: number,
    public hasProbateDefinitions?: boolean,
    public probateFallbackContent?: string
  ) { }

  static fromJSON(json: StateContentJson): StateContent {
    return new StateContent(
      json?.name,
      json?.code,
      json?.id,
      json?.states_id,
      json?.language ? Language.fromJSON(json.language) : undefined,
      json?.value_threshold,
      json?.has_probate_definitions,
      json?.probate_fallback_content
    );
  }

  toJSON(): StateContentJson {
    return {
      name: this.name,
      code: this.code,
      id: this.id,
      states_id: this.stateId,
      language: this?.language?.toJSON(),
      value_threshold: this?.valueThreshold,
      has_probate_definitions: this?.hasProbateDefinitions,
      probate_fallback_content: this?.probateFallbackContent,
    };
  }
}

export interface GeographyJson {
  id: number;
  name: string;
  slug: string;
  geography_type?: GeographyType;
  states?: GeographyJson[]; // Only for Country
  code?: string; // Only for Country and State
  content?: StateContentJson[]; // Only for State
  countries_id?: number; // Only for State
  country?: GeographyJson; // Only for State
  counties?: GeographyJson[]; // Only for State
  state_id?: number; // Only for County
  state?: GeographyJson; // Only for County
  county_type_id?: number; // Only for County
  court_type_id?: number; // Only for County
}

export class Geography {
  constructor(
    public id: number,
    public name: string,
    public fullName: string,
    public slug: string,
    public geographyType?: GeographyType
  ) { }

  toJSON(): GeographyJson {
    return {
      id: this.id,
      name: this.name,
      slug: this.slug,
      geography_type: this.geographyType,
    };
  }
}

export class Country extends Geography {
  constructor(
    public id: number,
    public name: string,
    public fullName: string,
    public code: string,
    public slug: string,
    public states?: State[],
    public geographyType?: GeographyType
  ) {
    super(id, name, fullName, slug, geographyType);
  }

  toJSON(): GeographyJson {
    const retJson = {
      id: this.id,
      name: this.name,
      code: this.code,
      slug: this.slug,
      geography_type: this.geographyType,
      states: this.states
        ? this.states.map((state) => state.toJSON())
        : undefined,
    };
    return retJson;
  }

  static fromJSON(json: GeographyJson): Country {
    if (!json.code) {
      throw new Error("Country code is missing");
    }
    const states: State[] = [];
    if (json.states) {
      for (const state of json.states) {
        states.push(State.fromJSON(state));
      }
    }
    return new Country(
      json.id,
      json.name,
      json.name,
      json.code,
      json.slug,
      json.states ? states : undefined,
      json.geography_type
    );
  }
}

export class State extends Geography {
  constructor(
    public id: number,
    public name: string,
    public fullName: string,
    public code: string,
    public slug: string,
    public content: StateContent,
    public countryId: number,
    public country?: Country,
    public counties?: County[],
    public geographyType?: GeographyType
  ) {
    super(id, name, fullName, slug, geographyType);
  }

  toJSON(): GeographyJson {
    const retJson = {
      id: this.id,
      name: this.name,
      code: this.code,
      slug: this.slug,
      geography_type: this.geographyType,
      content: [this.content.toJSON()],
      countries_id: this.countryId,
      country: this.country?.toJSON(),
      counties: this.counties
        ? this.counties.map((county) => county.toJSON())
        : undefined,
    };
    return retJson;
  }

  static fromJSON(json: GeographyJson): State {
    if (!json.content) {
      throw new Error("State's states_content is missing");
    }
    if (!json.countries_id) {
      throw new Error("State's countries_id is missing");
    }
    const counties: County[] = [];
    if (json.counties) {
      for (const county of json.counties) {
        counties.push(County.fromJSON(county));
      }
    }

    // Get the State Content to use based on current user's language.
    let contentToUse = json.content[0];
    for (let i = 0; i < json.content.length; i++) {
      const currentContent: StateContentJson = json.content[i];
      if (currentContent?.languages_id === 1) {
        contentToUse = currentContent;
        break;
      }
    }
    const stateContent = StateContent.fromJSON(contentToUse);

    return new State(
      json.id,
      stateContent?.name,
      json.country?.name
        ? `${stateContent.name}, ${json.country?.code}`
        : stateContent?.name,
      stateContent.code,
      json.slug,
      stateContent,
      json.countries_id,
      json.country ? Country.fromJSON(json.country) : undefined,
      json.counties ? counties : undefined,
      json.geography_type
    );
  }
}

export interface CountyJSON {
  id: number;
  name: string;
  slug: string;
}

export class County extends Geography {
  constructor(
    public id: number,
    public name: string,
    public fullName: string,
    public slug: string,
    public stateId?: number,
    public state?: State,
    public geographyType?: GeographyType,
    public countyTypeId?: number,
    public courtTypeId?: number
  ) {
    super(id, name, fullName, slug, geographyType);
  }

  toJSON(): GeographyJson {
    const retJson = {
      id: this.id,
      name: this.name,
      slug: this.slug,
      geography_type: this.geographyType,
      state_id: this.stateId,
      state: this.state?.toJSON(),
    };
    return retJson;
  }

  static fromJSON(json: GeographyJson): County {
    // Get the State Content to use based on current user's language.
    let contentToUse;
    if (json.state && json.state.content) {
      contentToUse = json.state?.content?.[0];
      for (let i = 0; i < json.state?.content.length; i++) {
        const currentContent: StateContentJson = json.state.content[i];
        if (currentContent?.languages_id === 1) {
          contentToUse = currentContent;
          break;
        }
      }
    }

    return new County(
      json.id,
      json.name,
      contentToUse ? `${json.name}, ${contentToUse.code}` : json.name,
      json.slug,
      json.state_id,
      json.state ? State.fromJSON(json.state) : undefined,
      json.geography_type
    );
  }
}

export interface CountyTypeJSON {
  id: number;
  type: string;
}

export class CountyType {
  constructor(public id: number, public type?: string) { }
  static fromJSON(json: CountyTypeJSON): CountyType {
    return new CountyType(json.id, json.type);
  }
}

export interface CourtTypeJSON {
  id: number;
  type: string;
}

export class CourtType {
  constructor(public id: number, public type?: string) { }
  static fromJSON(json: CourtTypeJSON): CourtType {
    return new CourtType(json.id, json.type);
  }
}
